import React, { createRef, useEffect, useMemo, useState } from 'react';
import styles from './emoji-control.module.scss';
import EmojiPickerReact from 'emoji-picker-react';
import { Emoji } from './Emoji';
import { EmojiPickerButton } from './EmojiPickerButton';
import Mustache from 'mustache';
import classnames from 'classnames';
import {
  Body_2,
  Body_4,
  FormError,
  Loader,
  Small_1,
  useThemeContext,
} from '@huggg/react-components';

export const EmojiControl = ({
  value,
  onEmojiSelected,
  sender = '',
  solo = false,
  loading = false,
  error = '',
}) => {
  const { reaction } = useThemeContext();

  const ref = createRef();

  const [showPicker, setShowPicker] = useState(false);

  const hasPickedEmoji = Boolean(value);

  const onOutsideClick = (e) => {
    const node = ref.current;
    const active = document.activeElement;
    const ariaControls = active?.getAttribute('aria-controls');

    if (
      active?.classList.contains('epr-search') ||
      active?.classList.contains('epr-btn') ||
      ariaControls === 'epr-search-id'
    ) {
      return;
    }

    if (!(node && node.contains(e.target)) ?? false) {
      setShowPicker(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', onOutsideClick, true);
    return () => {
      document.removeEventListener('click', onOutsideClick, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickerSubtitle = useMemo(() => {
    return sender
      ? Mustache.render(reaction.copy.picker.subtitle.template, { sender })
      : reaction.copy.picker.subtitle.static;
  }, [reaction.copy.picker.subtitle, sender]);

  const renderQuickEmojiBar = () => (
    <div className={styles.emojiBarContainer}>
      <div
        className={classnames(styles.quickEmojiBar, {
          [styles.largeEmojiBar]: solo,
        })}
      >
        {reaction.quickEmojis.map((emoji, index) => (
          <Emoji
            key={emoji + index}
            value={emoji}
            disabled={showPicker}
            onClick={onEmojiSelected}
          />
        ))}
        <EmojiPickerButton
          isOpen={showPicker}
          onClick={() => {
            setShowPicker(!showPicker);
          }}
        />
      </div>
      <FormError className={styles.error}>{error}</FormError>
    </div>
  );

  const renderEmojiPicker = () => (
    <div
      className={classnames(styles.picker, {
        [styles.showPicker]: showPicker,
      })}
    >
      <div className={styles.pickerCopy}>
        <Body_4>{reaction.copy.picker.title}</Body_4>
        <Small_1>{pickerSubtitle}</Small_1>
      </div>
      <EmojiPickerReact
        emojiStyle="native"
        previewConfig={{
          showPreview: false,
        }}
        width={264}
        onEmojiClick={({ emoji }) => {
          onEmojiSelected(emoji);
          setShowPicker(false);
        }}
      />
    </div>
  );

  const renderSelectedEmoji = () => (
    <div className={styles.selectedEmoji}>
      <Emoji value={`${value}`} size="32px" />
    </div>
  );

  return (
    <div ref={ref} style={{ width: 'fit-content' }}>
      <Body_2
        className={classnames(styles.container, {
          [styles.soloPicked]: solo && hasPickedEmoji,
          [styles.solo]: solo,
        })}
      >
        {loading && (
          <Loader
            className={classnames(styles.loader, {
              [styles.soloLoader]: solo,
            })}
          />
        )}

        <div
          className={classnames(styles.main, {
            [styles.selectedPaddingTop]: solo && hasPickedEmoji,
            [styles.hidden]: loading,
          })}
        >
          {!hasPickedEmoji && (
            <Small_1 className={styles.message}>Pick an emoji</Small_1>
          )}
          <div className={styles.pickerRow}>
            {hasPickedEmoji ? renderSelectedEmoji() : renderQuickEmojiBar()}
          </div>
        </div>
        {renderEmojiPicker()}
      </Body_2>
    </div>
  );
};
